import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import './InscriptionSubmissions.css';

const InscriptionSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.request('/inscription/submissions');
      if (response.success) {
        setSubmissions(response.submissions);
      } else {
        setError('Erreur lors du chargement des demandes');
      }
    } catch (err) {
      setError('Erreur de connexion');
      console.error('Error fetching inscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await apiClient.request(`/inscription/submissions/${id}`, {
        method: 'PUT'
      });
      fetchSubmissions();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      await apiClient.request(`/inscription/submissions/${id}`, {
        method: 'DELETE'
      });
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (err) {
      console.error('Error deleting submission:', err);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'unread') return sub.is_read === 0;
    if (filter === 'read') return sub.is_read === 1;
    return true;
  });

  const unreadCount = submissions.filter(s => s.is_read === 0).length;

  if (loading) {
    return (
      <div className="submissions-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement des demandes d'inscription...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="submissions-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>{error}</p>
        <button onClick={fetchSubmissions} className="retry-btn">
          <i className="fas fa-redo"></i> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="inscription-submissions">
      <div className="submissions-header">
        <h2>
          <i className="fas fa-user-graduate"></i>
          Demandes d'Inscription
          {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
        </h2>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={viewMode === 'table' ? 'active' : ''} 
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <i className="fas fa-table"></i>
            </button>
            <button 
              className={viewMode === 'cards' ? 'active' : ''} 
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <i className="fas fa-th-large"></i>
            </button>
          </div>
          <div className="filter-buttons">
            <button 
              className={filter === 'all' ? 'active' : ''} 
              onClick={() => setFilter('all')}
            >
              Toutes ({submissions.length})
            </button>
            <button 
              className={filter === 'unread' ? 'active' : ''} 
              onClick={() => setFilter('unread')}
            >
              Non lues ({unreadCount})
            </button>
            <button 
              className={filter === 'read' ? 'active' : ''} 
              onClick={() => setFilter('read')}
            >
              Lues ({submissions.length - unreadCount})
            </button>
          </div>
          <button onClick={fetchSubmissions} className="refresh-btn">
            <i className="fas fa-sync-alt"></i> Actualiser
          </button>
        </div>
      </div>

      {filteredSubmissions.length === 0 ? (
        <div className="no-submissions">
          <i className="fas fa-inbox"></i>
          <p>Aucune demande d'inscription pour le moment</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-view">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Ville</th>
                <th>Formation</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map(submission => (
                <tr 
                  key={submission.id} 
                  className={submission.is_read ? 'read' : 'unread'}
                >
                  <td>
                    {submission.is_read === 0 && <span className="status-dot unread"></span>}
                    {submission.is_read === 1 && <span className="status-dot read"></span>}
                  </td>
                  <td>
                    <strong>{submission.nom}</strong>
                  </td>
                  <td>
                    {submission.email ? (
                      <a href={`mailto:${submission.email}`}>{submission.email}</a>
                    ) : (
                      <span className="empty">—</span>
                    )}
                  </td>
                  <td>
                    {submission.telephone ? (
                      <a href={`tel:${submission.telephone}`}>{submission.telephone}</a>
                    ) : (
                      <span className="empty">-</span>
                    )}
                  </td>
                  <td>{submission.ville || <span className="empty">-</span>}</td>
                  <td>
                    <span className="formation-badge-mini">{submission.formation}</span>
                  </td>
                  <td className="date-cell">
                    {new Date(submission.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => {
                          setSelectedSubmission(submission);
                          if (submission.is_read === 0) {
                            markAsRead(submission.id);
                          }
                        }}
                        className="view-btn"
                        title="Voir les détails"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {submission.is_read === 0 && (
                        <button
                          onClick={() => markAsRead(submission.id)}
                          className="check-btn"
                          title="Marquer comme lue"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                      )}
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="trash-btn"
                        title="Supprimer"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="submissions-layout">
          <div className="submissions-list">
            {filteredSubmissions.map(submission => (
              <div
                key={submission.id}
                className={`submission-card ${submission.is_read ? 'read' : 'unread'} ${selectedSubmission?.id === submission.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedSubmission(submission);
                  if (submission.is_read === 0) {
                    markAsRead(submission.id);
                  }
                }}
              >
                <div className="card-header">
                  <div className="card-title">
                    {submission.is_read === 0 && <span className="unread-dot"></span>}
                    <strong>{submission.nom}</strong>
                  </div>
                  <span className="card-date">
                    {new Date(submission.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <div className="card-info">
                  <p className="formation-tag">
                    <i className="fas fa-graduation-cap"></i>
                    {submission.formation}
                  </p>
                  <p className="email-preview">
                    <i className="fas fa-envelope"></i>
                    {submission.email || '—'}
                  </p>
                  {submission.ville && (
                    <p className="ville-preview">
                      <i className="fas fa-map-marker-alt"></i>
                      {submission.ville}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="submission-detail">
            {selectedSubmission ? (
              <>
                <div className="detail-header">
                  <h3>Détails de la demande</h3>
                  <div className="detail-actions">
                    {selectedSubmission.is_read === 0 && (
                      <button
                        onClick={() => markAsRead(selectedSubmission.id)}
                        className="mark-read-btn"
                      >
                        <i className="fas fa-check"></i> Marquer comme lue
                      </button>
                    )}
                    <button
                      onClick={() => deleteSubmission(selectedSubmission.id)}
                      className="delete-btn"
                    >
                      <i className="fas fa-trash"></i> Supprimer
                    </button>
                  </div>
                </div>

                <div className="detail-content">
                  <div className="info-group">
                    <label><i className="fas fa-user"></i> Nom complet</label>
                    <p>{selectedSubmission.nom}</p>
                  </div>

                  <div className="info-group">
                    <label><i className="fas fa-envelope"></i> Email</label>
                    <p>
                      {selectedSubmission.email ? (
                        <a href={`mailto:${selectedSubmission.email}`}>
                          {selectedSubmission.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </p>
                  </div>

                  {selectedSubmission.telephone && (
                    <div className="info-group">
                      <label><i className="fas fa-phone"></i> Téléphone</label>
                      <p>
                        <a href={`tel:${selectedSubmission.telephone}`}>
                          {selectedSubmission.telephone}
                        </a>
                      </p>
                    </div>
                  )}

                  {selectedSubmission.ville && (
                    <div className="info-group">
                      <label><i className="fas fa-map-marker-alt"></i> Ville</label>
                      <p>{selectedSubmission.ville}</p>
                    </div>
                  )}

                  <div className="info-group">
                    <label><i className="fas fa-graduation-cap"></i> Formation demandée</label>
                    <p className="formation-badge">{selectedSubmission.formation}</p>
                  </div>

                  {selectedSubmission.message && (
                    <div className="info-group">
                      <label><i className="fas fa-comment"></i> Message</label>
                      <p className="message-content">{selectedSubmission.message}</p>
                    </div>
                  )}

                  <div className="info-group">
                    <label><i className="fas fa-calendar"></i> Reçu le</label>
                    <p>{new Date(selectedSubmission.created_at).toLocaleString('fr-FR')}</p>
                  </div>

                  <div className="info-group">
                    <label><i className="fas fa-info-circle"></i> Statut</label>
                    <p>
                      <span className={`status-badge ${selectedSubmission.is_read ? 'read' : 'unread'}`}>
                        {selectedSubmission.is_read ? 'Lue' : 'Non lue'}
                      </span>
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <i className="fas fa-hand-pointer"></i>
                <p>Sélectionnez une demande pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal for Table View */}
      {viewMode === 'table' && selectedSubmission && (
        <div className="detail-modal" onClick={() => setSelectedSubmission(null)}>
          <div className="detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-info-circle"></i> Détails de la demande
              </h3>
              <button 
                className="modal-close" 
                onClick={() => setSelectedSubmission(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-content">
                <div className="info-group">
                  <label><i className="fas fa-user"></i> Nom complet</label>
                  <p>{selectedSubmission.nom}</p>
                </div>

                <div className="info-group">
                  <label><i className="fas fa-envelope"></i> Email</label>
                  <p>
                    {selectedSubmission.email ? (
                      <a href={`mailto:${selectedSubmission.email}`}>
                        {selectedSubmission.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </p>
                </div>

                {selectedSubmission.telephone && (
                  <div className="info-group">
                    <label><i className="fas fa-phone"></i> Téléphone</label>
                    <p>
                      <a href={`tel:${selectedSubmission.telephone}`}>
                        {selectedSubmission.telephone}
                      </a>
                    </p>
                  </div>
                )}

                {selectedSubmission.ville && (
                  <div className="info-group">
                    <label><i className="fas fa-map-marker-alt"></i> Ville</label>
                    <p>{selectedSubmission.ville}</p>
                  </div>
                )}

                <div className="info-group">
                  <label><i className="fas fa-graduation-cap"></i> Formation demandée</label>
                  <p className="formation-badge">{selectedSubmission.formation}</p>
                </div>

                {selectedSubmission.message && (
                  <div className="info-group">
                    <label><i className="fas fa-comment"></i> Message</label>
                    <p className="message-content">{selectedSubmission.message}</p>
                  </div>
                )}

                <div className="info-group">
                  <label><i className="fas fa-calendar"></i> Reçu le</label>
                  <p>{new Date(selectedSubmission.created_at).toLocaleString('fr-FR')}</p>
                </div>

                <div className="info-group">
                  <label><i className="fas fa-info-circle"></i> Statut</label>
                  <p>
                    <span className={`status-badge ${selectedSubmission.is_read ? 'read' : 'unread'}`}>
                      {selectedSubmission.is_read ? 'Lue' : 'Non lue'}
                    </span>
                  </p>
                </div>
              </div>

              <div className="modal-actions">
                {selectedSubmission.is_read === 0 && (
                  <button
                    onClick={() => {
                      markAsRead(selectedSubmission.id);
                      setSelectedSubmission(null);
                    }}
                    className="mark-read-btn"
                  >
                    <i className="fas fa-check"></i> Marquer comme lue
                  </button>
                )}
                <button
                  onClick={() => {
                    deleteSubmission(selectedSubmission.id);
                    setSelectedSubmission(null);
                  }}
                  className="delete-btn"
                >
                  <i className="fas fa-trash"></i> Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InscriptionSubmissions;
